import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { SubmitReviewDialog } from '@/features/reviewPack'
import { mayTimeCard, startWorkNow, workKeys, type ActiveWork } from '@/features/worktime/api'
import type { Card } from '@/types/entities'
import { CompleteWorkDialog } from './CompleteWorkDialog'

/**
 * The three moments a stage change should never be silent.
 *
 * - **start**: work has begun, so ask to press play. This used to be a toast that vanished after twelve seconds, and
 *   nobody clicked it; a dialog has to be answered.
 * - **complete** and **review**: the drag, the Sheet and the menus used to move the item straight across. These ask
 *   for what the move needs (photos for Done, the review pack for Approval pending) and only then change the stage.
 *
 * Raised from anywhere with `askStage`; the host in the app shell shows the dialog.
 */
export type StagePrompt = 'start' | 'complete' | 'review'

export function askStage(kind: StagePrompt, card: Card) {
  window.dispatchEvent(new CustomEvent('tapwork:stage-prompt', { detail: { kind, card } }))
}

export function StagePromptHost() {
  const queryClient = useQueryClient()
  const { userId } = useIdentity()
  const [prompt, setPrompt] = useState<{ kind: StagePrompt; card: Card } | null>(null)

  useEffect(() => {
    const on = (event: Event) => {
      const { kind, card } = (event as CustomEvent<{ kind: StagePrompt; card: Card }>).detail
      if (kind === 'start') {
        if (!mayTimeCard(card, userId)) return
        const active = queryClient.getQueryData<ActiveWork>(workKeys.active)
        if (active?.session && !active.session.endedAt && active.session.cardId === card.id) return
      }
      setPrompt({ kind, card })
    }
    window.addEventListener('tapwork:stage-prompt', on)
    return () => window.removeEventListener('tapwork:stage-prompt', on)
  }, [queryClient, userId])

  if (!prompt) return null
  const close = () => setPrompt(null)
  const { card } = prompt
  if (prompt.kind === 'complete') return <CompleteWorkDialog card={card} open onOpenChange={(o) => !o && close()} />
  if (prompt.kind === 'review') return <SubmitReviewDialog card={card} open onOpenChange={(o) => !o && close()} />

  const active = queryClient.getQueryData<ActiveWork>(workKeys.active)
  const other = active?.session && !active.session.endedAt && active.card?.id !== card.id ? active.card : null
  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Play className="size-4 text-brand" /> Start work on {card.code ?? card.title}?</DialogTitle>
          <DialogDescription>
            Press play so your time on “{card.title}” is counted and your team can see what you are on.
            {other ? ` This stops your timer on ${other.code ?? other.title}.` : ''}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>Not now</Button>
          <Button type="button" autoFocus onClick={() => { void startWorkNow(queryClient, card.id); close() }}>
            <Play className="size-3.5" /> Start work
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
